import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Database,
  Stethoscope,
  Layers,
} from 'lucide-react';

type Lane = 'cpt' | 'icd10' | 'ncci';

const LANE_META: Record<Lane, { title: string; description: string; icon: React.ComponentType<{ className?: string }>; targetTable: string }> = {
  cpt: {
    title: 'CPT Master List',
    description: 'Maps to cpt_catalog. Upserts on the CPT code. Forbidden columns (Full Descriptor, Copyrighted Text) are stripped automatically.',
    icon: Stethoscope,
    targetTable: 'cpt_catalog',
  },
  icd10: {
    title: 'ICD-10 Set',
    description: 'Maps to icd10_catalog. Upserts on the ICD-10 code. Supports up to 5,000 rows per batch.',
    icon: Database,
    targetTable: 'icd10_catalog',
  },
  ncci: {
    title: 'NCCI Bundling Rules',
    description: 'Maps to cpt_pair_rules. Upserts on (cpt_a, cpt_b). Use bundling_rationale and mutually_exclusive columns.',
    icon: Layers,
    targetTable: 'cpt_pair_rules',
  },
};

// Columns we never send to the server (CPT licensing zero-fact policy)
const FORBIDDEN_HEADERS = new Set([
  'full descriptor',
  'fulldescriptor',
  'long descriptor',
  'longdescriptor',
  'copyrighted text',
  'copyrightedtext',
  'ama descriptor',
  'amadescriptor',
]);

function normalizeHeader(h: string): string {
  return String(h || '').toLowerCase().trim().replace(/[_\\-]+/g, ' ');
}

const BATCH_SIZE = 500;

interface ParsedState {
  headers: string[];
  strippedHeaders: string[];
  rows: Record<string, unknown>[];
  fileName: string;
}

interface UploadResult {
  inserted: number;
  skipped: number;
  errors: { line: number; message: string }[];
}

interface SpecialtyDomain {
  id: string;
  name: string;
}

function LaneImporter({ lane }: { lane: Lane }) {
  const meta = LANE_META[lane];
  const Icon = meta.icon;

  const [parsed, setParsed] = useState<ParsedState | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [domains, setDomains] = useState<SpecialtyDomain[]>([]);
  const [specialtyDomainId, setSpecialtyDomainId] = useState<string>('');

  // Load specialty domains for the NCCI lane (isolation enforcement).
  useEffect(() => {
    if (lane !== 'ncci') return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('specialty_domains')
        .select('id, name')
        .order('name');
      if (cancelled) return;
      if (error) {
        toast.error(`Failed to load specialty domains: ${error.message}`);
        return;
      }
      setDomains((data ?? []) as SpecialtyDomain[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [lane]);

  const onDrop = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    setResult(null);
    setProgress(0);

    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const originalHeaders = results.meta.fields ?? [];
        const stripped: string[] = [];
        const kept: string[] = [];
        for (const h of originalHeaders) {
          if (FORBIDDEN_HEADERS.has(normalizeHeader(h))) {
            stripped.push(h);
          } else {
            kept.push(h);
          }
        }

        // Strip forbidden columns from every row
        const cleanRows = results.data.map((row) => {
          const next: Record<string, unknown> = {};
          for (const k of kept) next[k] = row[k];
          return next;
        });

        setParsed({
          headers: kept,
          strippedHeaders: stripped,
          rows: cleanRows,
          fileName: file.name,
        });

        if (stripped.length > 0) {
          toast.warning(`Stripped ${stripped.length} forbidden column(s) for CPT licensing compliance.`);
        }
      },
      error: (err) => {
        toast.error(`CSV parse failed: ${err.message}`);
      },
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    maxFiles: 1,
    disabled: uploading,
  });

  const previewRows = useMemo(() => parsed?.rows.slice(0, 5) ?? [], [parsed]);

  async function handleUpload() {
    if (!parsed) return;
    if (lane === 'ncci' && !specialtyDomainId) {
      toast.error('Select a Specialty Domain before importing NCCI rules.');
      return;
    }
    setUploading(true);
    setProgress(0);
    setResult(null);

    const aggregated: UploadResult = { inserted: 0, skipped: 0, errors: [] };
    const total = parsed.rows.length;
    let processed = 0;

    try {
      for (let i = 0; i < total; i += BATCH_SIZE) {
        const batch = parsed.rows.slice(i, i + BATCH_SIZE);
        const { data, error } = await supabase.functions.invoke('ingest-clinical-taxonomy', {
          body: {
            lane,
            rows: batch,
            ...(lane === 'ncci' && specialtyDomainId
              ? { specialty_domain_id: specialtyDomainId }
              : {}),
          },
        });
        if (error) throw new Error(error.message);
        const r = data as UploadResult & { ok?: boolean; error?: string };
        if ('error' in r && r.error) throw new Error(r.error);
        aggregated.inserted += r.inserted ?? 0;
        aggregated.skipped += r.skipped ?? 0;
        if (Array.isArray(r.errors)) aggregated.errors.push(...r.errors);

        processed += batch.length;
        setProgress(Math.round((processed / total) * 100));
        // Yield to keep UI responsive on huge batches
        await new Promise((r) => setTimeout(r, 0));
      }

      setResult(aggregated);
      if (aggregated.errors.length === 0) {
        toast.success(`Imported ${aggregated.inserted} rows into ${meta.targetTable}.`);
      } else {
        toast.warning(`Imported ${aggregated.inserted} rows; ${aggregated.errors.length} row error(s).`);
      }
    } catch (e) {
      toast.error(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  function reset() {
    setParsed(null);
    setProgress(0);
    setResult(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-primary" />
          {meta.title}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{meta.description}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {lane === 'ncci' && (
          <div className="space-y-2 bg-muted/30 border rounded-md p-4">
            <Label htmlFor="ncci-specialty-domain" className="text-xs font-semibold uppercase tracking-wide">
              Specialty Domain
            </Label>
            <Select
              value={specialtyDomainId}
              onValueChange={setSpecialtyDomainId}
              disabled={uploading}
            >
              <SelectTrigger id="ncci-specialty-domain" className="bg-background">
                <SelectValue placeholder="Select specialty (e.g., Podiatry, Orthopedic)…" />
              </SelectTrigger>
              <SelectContent>
                {domains.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Required. Every imported rule is scoped to this specialty so bundling
              logic never crosses domains (e.g., Podiatry rules won't fire on Ortho cases).
              Per-row <span className="font-mono">specialty_domain_id</span> in the CSV overrides this default.
            </p>
          </div>
        )}

        {!parsed && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer ${
              isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 bg-muted/30'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium">
              {isDragActive ? 'Drop the CSV here…' : 'Drag & drop a CSV, or click to browse'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Max 5,000 rows per batch. Forbidden columns are stripped automatically.
            </p>
          </div>
        )}

        {parsed && (
          <>
            <div className="flex items-center justify-between bg-muted/40 rounded-md px-4 py-3">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-medium">{parsed.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {parsed.rows.length.toLocaleString()} rows · {parsed.headers.length} columns
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={reset} disabled={uploading}>
                Replace file
              </Button>
            </div>

            {parsed.strippedHeaders.length > 0 && (
              <Alert>
                <ShieldCheck className="w-4 h-4" />
                <AlertTitle>CPT Licensing Guardrail</AlertTitle>
                <AlertDescription>
                  Stripped {parsed.strippedHeaders.length} forbidden column(s):{' '}
                  <span className="font-mono">{parsed.strippedHeaders.join(', ')}</span>
                </AlertDescription>
              </Alert>
            )}

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Preview — first 5 rows
              </p>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      {parsed.headers.map((h) => (
                        <TableHead key={h} className="h-9 text-xs font-semibold uppercase tracking-wide">
                          {h}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((row, i) => (
                      <TableRow key={i} className="text-xs">
                        {parsed.headers.map((h) => (
                          <TableCell key={h} className="py-2 font-mono">
                            {String(row[h] ?? '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Uploading to {meta.targetTable}…</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {result && (
              <Alert>
                {result.errors.length === 0 ? (
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                <AlertTitle>Import complete</AlertTitle>
                <AlertDescription>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <Badge variant="secondary">Inserted: {result.inserted.toLocaleString()}</Badge>
                    <Badge variant="outline">Skipped: {result.skipped.toLocaleString()}</Badge>
                    {result.errors.length > 0 && (
                      <Badge variant="destructive">Errors: {result.errors.length}</Badge>
                    )}
                  </div>
                  {result.errors.length > 0 && (
                    <details className="mt-3 text-xs">
                      <summary className="cursor-pointer font-medium">Show first 20 errors</summary>
                      <ul className="mt-2 space-y-1 font-mono">
                        {result.errors.slice(0, 20).map((e, i) => (
                          <li key={i}>
                            Line {e.line}: {e.message}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <Button
                onClick={handleUpload}
                disabled={uploading || parsed.rows.length === 0 || (lane === 'ncci' && !specialtyDomainId)}
                className="h-11 bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {parsed.rows.length.toLocaleString()} rows
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function TaxonomyImporter() {
  return (
    <Tabs defaultValue="cpt" className="space-y-4">
      <TabsList className="grid w-full grid-cols-3 h-auto">
        <TabsTrigger value="cpt" className="flex items-center gap-2 py-2">
          <Stethoscope className="w-4 h-4" />
          CPT Master
        </TabsTrigger>
        <TabsTrigger value="icd10" className="flex items-center gap-2 py-2">
          <Database className="w-4 h-4" />
          ICD-10 Set
        </TabsTrigger>
        <TabsTrigger value="ncci" className="flex items-center gap-2 py-2">
          <Layers className="w-4 h-4" />
          NCCI Pair Rules
        </TabsTrigger>
      </TabsList>

      <TabsContent value="cpt">
        <LaneImporter lane="cpt" />
      </TabsContent>
      <TabsContent value="icd10">
        <LaneImporter lane="icd10" />
      </TabsContent>
      <TabsContent value="ncci">
        <LaneImporter lane="ncci" />
      </TabsContent>
    </Tabs>
  );
}
